import { useEffect, useMemo, useState } from "react";
import TableWidget from "../../../components/tables/TableWidget";
import {
  getReturningProductsToProviderFromReceptionTableData,
  returningProductsToProviderFromReceptionTableHeader,
} from "./tableConfigs/returningProductsToProviderFromReceptionTableConfig";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { getQuarantinedProductsToReturn } from "../../../redux/api/inventory.api";
import { TableActions } from "../../../components/tables/tableContext/TableContext";
import { clearQuarantinedProductsToReturn } from "../../../redux/reducer/inventory.reducer";
import useTableCtx from "../../../components/tables/tableContext/useTableCtx";
import ActionMenu from "../../../components/actionMenu/ActionMenu";
import CustomButton from "../../../components/CustomButton";
import ReturningProductsModal from "./components/ReturningProductsFromProviderModal";

function ReturningProductsToProviderFromReception() {
  const dispatch = useAppDispatch();
  const {
    inventory: {
      quarantinedProductsToReturn: {
        data: { data: returningProducts, total: productsTotalNumber },
        isLoading,
      },
    },
  } = useAppSelector((state) => ({
    inventory: state.inventory,
  }));

  const [isInitiated, setIsInitiated] = useState(false);
  const [selectedProviderFilter, setSelectedProviderFilter] = useState<
    string | number
  >("0");
  const [providers, setProviders] = useState<{ id: number; label: string }[]>(
    [],
  );

  const [invoices, setInvoices] = useState<
    {
      codFactura: string;
      idDetaliuFactura: number;
      idFactura: number;
      idFurnizor: number;
    }[]
  >([]);

  const filteredInvoices = useMemo(() => {
    if (!selectedProviderFilter || Number(selectedProviderFilter) === 0) {
      return invoices;
    }

    return invoices.filter(
      (invoice) => invoice.idFurnizor === Number(selectedProviderFilter),
    );
  }, [invoices, selectedProviderFilter]);

  useEffect(() => {
    if (returningProducts.length > 0 && !isInitiated) {
      const allProvidersRaw = returningProducts.flatMap(
        (item) => item.furnizori ?? [],
      );
      const allProviders = [
        ...new Map(allProvidersRaw.map((p) => [p.id, p])).values(),
      ];
      const allInvoices = [
        ...new Map(
          allProvidersRaw
            .flatMap((p) => p.facturi ?? [])
            .map((f) => [`${f.idFurnizor}-${f.idFactura}`, f]),
        ).values(),
      ];

      setProviders(allProviders);
      setInvoices(allInvoices);
      setIsInitiated(true);
    }
  }, [isInitiated, returningProducts]);

  useEffect(() => {
    return () => {
      dispatch(clearQuarantinedProductsToReturn());
    };
  }, [dispatch]);

  const handleTableActions = (action: TableActions) => {
    if (
      action.tablePagination.page &&
      action.tablePagination.rowsPerPage &&
      action.tableSearch
    ) {
      const provider = action.tableSearch.provider || "0";
      const invoice = action.tableSearch.invoice || "0";
      const invoiceBelongsToSelectedProvider =
        Number(provider) === 0 ||
        invoices.some(
          (item) =>
            item.idFurnizor === Number(provider) &&
            item.idFactura === Number(invoice),
        );

      setSelectedProviderFilter(provider);

      dispatch(
        getQuarantinedProductsToReturn({
          page: 1,
          perPage: 10000,
          codProdus: action.tableSearch.codProdus,
          numeProdusErp: action.tableSearch.numeProdusErp,
          invoice: invoiceBelongsToSelectedProvider ? invoice : "0",
          provider,
        }),
      );
    }
  };

  const CustomHeaderComponent = () => {
    const { handleCheckAllItems, selectedItems, setSelectedItems } =
      useTableCtx()!;

    return (
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <CustomButton
            variant="outlined"
            color="inherit"
            icon="CHECKBOX"
            sx={{ borderColor: "divider" }}
            onClick={handleCheckAllItems}>
            Selecteaza toate
          </CustomButton>
        </div>
        <div className="ml-auto">
          <ActionMenu
            minItemsForMenu={10}
            ariaLabel="Actiuni produse selectate"
            items={[
              {
                id: "return",
                label: "Returnare",
                icon: "RETURNING_DOCUMENT",
                tooltip:
                  "Deschide formularul de retur catre furnizor pentru produsele selectate.",
                hidden: !selectedItems.length,
                component: (
                  <ReturningProductsModal
                    selectedReturningProducts={selectedItems}
                    setSelectedItems={setSelectedItems}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  };

  return (
    <TableWidget
      tableTitle={"Retur catre furnizor (produse carantinate, eligibile)"}
      CustomHeaderComponent={CustomHeaderComponent}
      tableHeaderData={returningProductsToProviderFromReceptionTableHeader(
        providers,
        filteredInvoices,
      )}
      tableItemsData={getReturningProductsToProviderFromReceptionTableData(
        returningProducts,
      )}
      handleTableActions={handleTableActions}
      totalItems={productsTotalNumber}
      tableLoading={isLoading}
      withGlobalSearch={false}
      withGlobalFilter={false}
      withTablePagination={false}
    />
  );
}

export default ReturningProductsToProviderFromReception;
